import React, { useState } from 'react';
import { errorMessage, toastError, toastSuccess } from '../../lib/toast';
import { acknowledgeOutside, createProposal, uploadDocument } from './vendorsApi';
import { DOC_TYPE_LABEL, VENDOR_ERRORS, atLeast } from './vendorLabels';
import { Dialog } from './VendorBits';
import VendorForm from './VendorForm';
import MarkReviewedDialog from './MarkReviewedDialog';

const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPTED = ['application/pdf', 'image/jpeg', 'image/png'];

/** Upload a document (propose level). The server re-checks type by magic bytes. */
export function UploadDocumentDialog({ vendorId, onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [docType, setDocType] = useState('');
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const pick = (e) => setFile(e.target.files?.[0] || null);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const found = {};
    if (!file) found.file = 'Choose a file.';
    else if (file.size > MAX_BYTES) found.file = VENDOR_ERRORS.file_too_large;
    else if (file.type && !ACCEPTED.includes(file.type)) found.file = VENDOR_ERRORS.bad_file_type;
    if (!docType) found.type = 'Choose a document type.';
    setErrors(found);
    if (Object.keys(found).length) return;
    setBusy(true);
    try {
      await uploadDocument(vendorId, file, docType);
      toastSuccess('Document uploaded.');
      onUploaded();
    } catch (err) {
      toastError(errorMessage(err, 'Could not upload the document.', VENDOR_ERRORS));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog title="Upload document" onClose={onClose} labelledBy="vd-upload-title">
      <form onSubmit={submit} noValidate>
        <div className="vd-form-row">
          <label htmlFor="vd-upload-file">File</label>
          <input id="vd-upload-file" type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" onChange={pick} disabled={busy} />
          {errors.file && <p className="vd-field-error" role="alert">{errors.file}</p>}
        </div>
        <div className="vd-form-row">
          <label htmlFor="vd-upload-type">Document type</label>
          <select id="vd-upload-type" value={docType} onChange={(e) => setDocType(e.target.value)} disabled={busy}>
            <option value="">Select a type</option>
            {Object.entries(DOC_TYPE_LABEL).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
          {errors.type && <p className="vd-field-error" role="alert">{errors.type}</p>}
        </div>
        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={busy} aria-busy={busy || undefined}>Upload</button>
        </div>
      </form>
    </Dialog>
  );
}

export function UploadDocumentButton({ vendorId, onUploaded }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="vd-text-btn" onClick={() => setOpen(true)}>Upload document</button>
      {open && <UploadDocumentDialog vendorId={vendorId} onClose={() => setOpen(false)} onUploaded={() => { setOpen(false); onUploaded(); }} />}
    </>
  );
}

function ArchiveDialog({ vendor, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    setBusy(true);
    try {
      await createProposal({ kind: 'archive', vendor_id: vendor.id });
      toastSuccess('Change proposed — the speciality owner will review it.');
      onDone();
    } catch (err) {
      toastError(errorMessage(err, 'Could not submit the proposal.', VENDOR_ERRORS));
      setBusy(false);
    }
  };
  return (
    <Dialog title={`Propose archiving ${vendor.name}?`} onClose={onClose} labelledBy="vd-archive-title">
      <p>Nothing changes yet. The speciality owner must approve before it&apos;s archived in Odoo.</p>
      <div className="form-actions">
        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className="btn btn-danger" onClick={confirm} disabled={busy} aria-busy={busy || undefined}>Propose archive</button>
      </div>
    </Dialog>
  );
}

/** Who can do what on this vendor (spec 4.2 level table). */
export function vendorPermissions(vendor, level) {
  const active = vendor.active !== false;
  return {
    canPropose: atLeast(level, 'propose') && active,
    canApprove: atLeast(level, 'approve'),
    canReview: atLeast(level, 'approve') && active,
  };
}

/** Acknowledging an outside change; a 409 means someone already did, so refresh either way. */
export async function acknowledgeVendor(vendorId, onDone) {
  try {
    await acknowledgeOutside(vendorId);
    toastSuccess('Acknowledged.');
    onDone();
  } catch (err) {
    toastError(errorMessage(err, 'Could not acknowledge the change.', VENDOR_ERRORS));
    if (err?.response?.status === 409 || err?.response?.data?.error === 'nothing_to_acknowledge') onDone();
  }
}

/**
 * The detail header's actions. `panel` is owned by the page so the
 * attention rows can open the same drawers and dialogs.
 */
export default function VendorActions({
  vendor, level, me, isoFieldsKnown, panel, onPanel, onReviewed, onProposed,
}) {
  const { canPropose, canReview } = vendorPermissions(vendor, level);
  const close = () => onPanel(null);

  return (
    <>
      {(canPropose || canReview) && (
        // One primary action; archiving is rare and destructive, so it sits last as quiet danger text.
        <div className="vd-actions" aria-label="Vendor actions">
          {canReview && (
            <button type="button" className="btn btn-secondary vd-btn-compact" onClick={() => onPanel('review')}>Mark reviewed</button>
          )}
          {canPropose && <button type="button" className="btn btn-primary vd-btn-compact" onClick={() => onPanel('edit')}>Propose changes</button>}
          {canPropose && <button type="button" className="vd-text-btn vd-text-btn--danger" onClick={() => onPanel('archive')}>Propose archive</button>}
        </div>
      )}
      {panel === 'edit' && (
        <VendorForm open mode="edit" vendor={vendor} me={me} isoFieldsKnown={isoFieldsKnown} onClose={close} onSubmitted={() => { close(); onProposed(); }} />
      )}
      {panel === 'archive' && <ArchiveDialog vendor={vendor} onClose={close} onDone={() => { close(); onProposed(); }} />}
      {panel === 'review' && (
        <MarkReviewedDialog vendor={vendor} onClose={close} onDone={() => { close(); onReviewed(); }} />
      )}
    </>
  );
}
